import React, { useState, useEffect } from 'react';
import { Button, IconButton, SearchInput, Tooltip, COLORS } from 'pvs-design-system';
import { Box } from '@mui/material';
import ManageSearch from '@mui/icons-material/ManageSearch';

export type SearchInValue = 'all' | 'category' | 'name' | 'description';

export const SEARCH_IN_OPTIONS: { value: SearchInValue; label: string }[] = [
  { value: 'all', label: 'All fields' },
  { value: 'name', label: 'Resource' },
  { value: 'category', label: 'Folder' },
  { value: 'description', label: 'Description' },
];

/** "Match mode" for multi-word queries — shared by any page that shows the
 *  Advanced Search Filters popover (Index hero, Search results). */
export type SearchMode = 'all' | 'any' | 'exact';

export const SEARCH_MODE_LABELS: { value: SearchMode; label: string; description: string }[] = [
  { value: 'any', label: 'Match any word', description: 'Results contain at least one of your search terms' },
  { value: 'all', label: 'Match all words', description: 'Results contain every search term you entered' },
  { value: 'exact', label: 'Exact phrase', description: 'Results contain your search terms as written' },
];

interface LibrarySearchBarProps {
  /** Committed search text (e.g. from the URL `q`). Seeds the input and
   *  re-syncs the draft whenever it changes (on submit / navigation) — but
   *  NOT while typing, so keystrokes never re-render the parent page. */
  value?: string;
  placeholder?: string;
  /** Pressing Enter or clicking the Search button calls this with the current text. */
  onSearch?: (query: string) => void;
  /** Called when the user empties the input. */
  onClear?: () => void;
  /** large = IndexPage hero (h:60), compact = page header (h:38) */
  size?: 'large' | 'compact';
  /** Ref forwarded to the outermost container — used by caller for click-outside detection */
  containerRef?: React.RefObject<HTMLDivElement>;
  /** Ref forwarded to the text input — used for programmatic focus */
  inputRef?: React.RefObject<HTMLInputElement>;
  /** When provided, a filter icon button is shown */
  settingsOpen?: boolean;
  onSettingsToggle?: () => void;
  settingsLabel?: string;
}

const LibrarySearchBar: React.FC<LibrarySearchBarProps> = ({
  value = '',
  placeholder = 'Search Virtual Library',
  onSearch,
  onClear,
  size = 'compact',
  containerRef,
  inputRef,
  settingsOpen,
  onSettingsToggle,
  settingsLabel = 'Advanced search',
}) => {
  const isLarge = size === 'large';

  // Draft text is owned locally so typing re-renders ONLY this component, never
  // the parent page (and its result list). The committed `value` seeds it and
  // re-syncs it whenever the URL query changes (submit / back-forward).
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);

  const submitSearch = () => onSearch?.(draft.trim());

  // Forced explicitly on both elements so they match exactly regardless of the active theme's own size-token height.
  const fieldHeight = isLarge ? 48 : 40;
  const pillShadow = isLarge ? '0 2px 12px rgba(0,0,0,0.06)' : 'none';
  const focusRingSize = isLarge ? '4px' : '3px';
  const searchBtnPx = isLarge ? 2.5 : 2;

  return (
    <Box
      ref={containerRef}
      sx={{ display: 'flex', alignItems: 'center', gap: 0.75, position: 'relative', maxWidth: isLarge ? undefined : 610 }}
    >
      <SearchInput
        id="vl-search-input"
        name="search"
        ariaLabel="Search the virtual library"
        value={draft}
        onChange={(e) => {
          const v = e.target.value;
          setDraft(v);
          if (v === '' && onClear) onClear();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submitSearch();
        }}
        inputRef={inputRef}
        placeholder={placeholder}
        sx={{
          flex: 1,
          '& .MuiOutlinedInput-root': {
            height: fieldHeight,
            borderRadius: 999,
            background: COLORS.white,
            boxShadow: pillShadow,
            transition: 'border-color 0.15s, box-shadow 0.15s',
            '& fieldset': { borderColor: COLORS.gray[200], borderWidth: 2 },
            '&:hover fieldset': { borderColor: COLORS.gray[200] },
            '&.Mui-focused fieldset': {
              borderColor: COLORS.purple[700],
              borderWidth: 2,
              boxShadow: `0 0 0 ${focusRingSize} rgba(106,27,131,0.10)`,
            },
          },
        }}
      />

      {/* Search button (optional) — kept separate from SearchInput, forced to the same explicit height */}
      {onSearch && (
        <Button
          variant="contained"
          onClick={submitSearch}
          sx={{
            height: fieldHeight,
            background: COLORS.purple[700],
            '&:hover': { background: COLORS.purple[800] },
            borderRadius: 999, textTransform: 'none',
            fontWeight: 600, px: searchBtnPx,
            boxShadow: 'none', flexShrink: 0, minWidth: 0,
          }}
        >
          Search
        </Button>
      )}

      {/* Advanced search filters (optional) */}
      {onSettingsToggle && (
        <Tooltip title={settingsLabel} placement="bottom" arrow>
          <Box component="span">
            <IconButton
              variant={settingsOpen ? 'primary' : 'secondary'}
              onClick={onSettingsToggle}
              aria-label={settingsLabel}
              aria-expanded={settingsOpen}
              sx={{ color: COLORS.purple[700] }}
            >
              <ManageSearch />
            </IconButton>
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export default LibrarySearchBar;
